<template>
    <div class="position-relative" id="chatbot-dashboard">
        <MovePanel v-if="movingMode" :changeMoveMode="changeMoveMode" />
        <CopyPanel v-if="copyingMode" :changeCopyMode="changeCopyMode" />
        <DragPanel v-if="dragMode" />
        <FlowBreadCrumb
            :navigateTo="navigateToBlock"
            :navigation="getNavigationDetail()"
            :title="storyTitle"
            :storyId="storyId"
            :preview-mode="previewMode"
            :board="board"
            @showAIKnowledge="() => (showAIKnowledge = true)"
        />
        <FooterToolbar
            :scale="scale"
            @updateScale="(e) => (this.scale = e)"
            v-if="!dragMode"
            :focus-start-point="focusStartPoint"
            :board="board"
            :story-id="storyId"
            :story-version="storyVersion"
            :preview-mode="previewMode"
        />
        <Toolbar
            v-if="!block && !movingMode && !copyingMode"
            :dragMode="dragMode"
            :dragStart="dragStart"
            :dragEnd="dragEnd"
            @openModal="openModal"
            :addBlockToDiagram="addBlockToDiagram"
            @setTranslate="(e) => this.updateTranslate(e, true)"
            :flattedDiagram="flattedDiagram"
            :blocksMetas="blocksMetas"
            :story-id="storyId"
            :version="storyVersion"
            :test-start-block-id="testStartBlockId"
            :diagram="board.diagram"
            :board="board"
            :preview-mode="previewMode"
            :navigateTo="navigateToBlock"
        />
        <BlockModal
            ref="BlockModal"
            v-if="!!block && !movingMode && !copyingMode"
            :block="block"
            @close="closeModal"
            :storyId="storyId"
            :storyVersion="storyVersion"
            :flattedDiagram="flattedDiagram"
            :preview-mode="previewMode"
        />
        <AddContextMenu
            v-if="addMenu"
            :addMenuPos="addMenuPos"
            :block="addBlock"
            @closeContextMenu="closeModal"
            :addBlockToDiagram="addBlockToDiagram"
            :navigation="getNavigationDetail()"
            :storyId="storyId"
            :storyVersion="storyVersion"
            :showNotification="showNotification"
            :flattedDiagram="flattedDiagram"
            :blocksMetas="blocksMetas"
            :getAllowedBlocks="getAllowedBlocks"
        />
        <div style="overflow: hidden">
            <div class="overlay" v-if="overlayVisible" @click="closeModal(false)" />
            <DiagramViewer
                @openContextMenu="openAddContext"
                :translate="translate"
                :scale="scale"
                :board="board"
                :diagram="board.diagram"
                @updateScale="(e) => (this.scale = e)"
                :translating="translating"
                @updateTranslate="updateTranslate"
                @setTranslate="(e) => this.updateTranslate(e, true)"
                @openModal="openModal"
                :selectedBlock="!!block && block.id"
                :navigation="getNavigationDetail()"
                :navigateToBlock="navigateToBlock"
                :storyId="storyId"
                :storyVersion="storyVersion"
                :movingMode="movingMode"
                :movingBlock="movingBlock"
                :changeMoveMode="changeMoveMode"
                :moveBlock="moveBlock"
                :copyingMode="copyingMode"
                :copyingBlock="copyingBlock"
                :changeCopyMode="changeCopyMode"
                :copyBlock="copyBlock"
                :flattedDiagram="flattedDiagram"
                :showNotification="showNotification"
                :dragMode="dragMode"
                :addBlockToDiagram="addBlockToDiagram"
                :draggingType="draggingType"
                @blockMetasCalculated="updateBlockMetas($event)"
                :start-test-from-here="startTestFromHere"
                :test-state="chatWidgetOpened ? testState : null"
                :selected-block-id="selectedBlockId"
                :test-start-block-id="testStartBlockId"
                :preview-mode="previewMode"
                :getAllowedBlocks="getAllowedBlocks"
            />
            <AIKnowledge
                :storyId="storyId"
                :storyTitle="storyTitle"
                v-if="showAIKnowledge"
                @close="showAIKnowledge = false"
            />
        </div>
    </div>
</template>

<script>
import { log2 } from "../../../../../../utils/log2";
import _ from "lodash";
import Toolbar from "./layout/Toolbar";
import FooterToolbar from "./layout/FooterToolbar";
import BlockModal from "./layout/BlockModal";
import DiagramViewer from "./DiagramViewer";
import AddContextMenu from "../AddContextMenu";
import FlowBreadCrumb from "./layout/FlowBreadCrumb";
import MovePanel from "./layout/MovePanel";
import CopyPanel from "./layout/CopyPanel";
import { mapActions, mapGetters, mapState } from "vuex";
import { message } from "ant-design-vue";
import { Howl } from "howler";
import DragPanel from "./layout/DragPanel";
import { sendSocketMessage, socketMessageStream } from "../../../../../../utils/socketStream";
import { filter } from "rxjs/operators";
import BLOCK_TYPES from "../../../block.types";
import flatDiagram from "../../../utils/flatDiagram";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";
import AIKnowledge from "../../../ai/AIKnowledge.vue";
import StringUtils from "../../../../../../utils/StringUtils";

const log = log2("ChatBotDashboard");
const notificationSound = new Howl({
    src: [`/static/media/notification/pop-alert.mp3`],
    autoplay: false,
    loop: false,
    volume: 1,
    onplayerror: (ex) => console.error("showNotification pop-alert error!", ex),
});
const initialTranslate = {
    x: window.innerWidth / 5,
    y: window.innerHeight / 2,
};
export default {
    name: "ChatbotDashboard",
    components: {
        AIKnowledge,
        AddContextMenu,
        DiagramViewer,
        BlockModal,
        FooterToolbar,
        Toolbar,
        FlowBreadCrumb,
        MovePanel,
        CopyPanel,
        DragPanel,
    },
    props: ["board", "storyId", "storyVersion", "storyTitle"],
    computed: {
        ...mapState({
            userInteracted: (state) => state.app.userInteracted,
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
        }),
        ...mapGetters({
            gpt3Enabled: "setting/gpt3Enabled",
            getUsers: "user/getUsers",
        }),
        flattedDiagram: function () {
            log("flattedDiagram running", this.board);
            return flatDiagram(this.board.diagram);
        },
        previewMode: function () {
            return this.board.published || !!this.board.publishDate;
        },
    },
    data() {
        return {
            scale: 1,
            oldTranslate: _.cloneDeep(initialTranslate),
            translate: _.cloneDeep(initialTranslate),
            translating: false,
            block: null,
            overlayVisible: false,
            addMenuPos: { x: 0, y: 0, height: "auto" },
            addMenu: false,
            addBlock: null,
            movingMode: false,
            copyingMode: false,
            movingBlock: null,
            copyingBlock: null,
            dragMode: false,
            draggingType: null,
            blocksMetas: null,
            testStartBlockId: null,
            testState: null,
            selectedBlockId: null,
            chatWidgetOpened: false,
            userEntities: [],
            storyVisitSub: null,
            showAIKnowledge: false,
        };
    },
    methods: {
        ...mapActions({
            moveBlockD: "CB/moveBlock",
            copyBlockD: "CB/copyBlock",
            addBlockD: "CB/addBlock",
            fetchUserEntities: "CB/fetchUserEntities",
        }),
        updateBlockMetas(metas) {
            const isEqual = StringUtils.isDeepEqual(this.blocksMetas, metas);
            log("updateBlockMetas", "isEqual", isEqual, metas);
            if (!isEqual) {
                this.blocksMetas = metas;
                this.$nextTick();
                // if (this.updateBlockMetasTimerId) clearTimeout(this.updateBlockMetasTimerId);
                // this.updateBlockMetasTimerId = setTimeout(() => {
                //     this.blocksMetas = metas;
                //     this.$nextTick();
                // }, 100);
            }
        },
        extractAttributes(string) {
            const r = new RegExp(/{{\s*[\w\.]+\s*}}/gim);
            const matches = string.match(r) || [];
            return matches.map((match) => match.replace(/{{\s*|\s*}}/gim, ""));
        },
        getCustomAttributes() {
            let attrs = this.flattedDiagram.map((block) => {
                if (block.type === BLOCK_TYPES.BOT_RESPONSE) {
                    let items = block.payload || [];
                    items = items.map((item) => {
                        if (item.type === "TEXT") {
                            return [item.text];
                        } else if (item.type === "RANDOM_TEXT") {
                            return item.texts;
                        } else if (item.type === "GALLERY") {
                            return _.flatten(
                                item.cards.map((card) => [
                                    card.title,
                                    card.description,
                                    ...card.buttons.map((x) => x.title),
                                    ..._.flatten(
                                        card.buttons.map((x) =>
                                            x.actions
                                                .filter((x) => x.type === "SET_ATTRIBUTE")
                                                .map((x) => x.payload.attribute)
                                                .map((x) => `{{${x}}}`)
                                        )
                                    ),
                                ])
                            );
                        } else if (["QUICK_REPLY", "BUTTON"].includes(item.type)) {
                            return _.flatten([
                                item.message,
                                ...item.buttons.map((x) => x.title),
                                ..._.flatten(
                                    item.buttons.map((x) =>
                                        x.actions
                                            .filter((x) => x.type === "SET_ATTRIBUTE")
                                            .map((x) => x.payload.attribute)
                                            .map((x) => `{{${x}}}`)
                                    )
                                ),
                            ]);
                        }
                    });
                    log("getCustomAttributes -> BOT_RESPONSE", items, block);
                    return _.flatten(items);
                } else if (block.type === BLOCK_TYPES.SET_ATTRIBUTE) {
                    let items = block.payload || [];
                    items = items.map((x) => `{{${x.attribute}}}`);
                    log("getCustomAttributes -> SET_ATTRIBUTE", items, block);
                    return items;
                } else if (block.type === BLOCK_TYPES.QUESTION) {
                    let items = block.payload || [];
                    items.map((x) => {
                        return [...x.questions, `{{${x.set}}`];
                    });
                    items = _.flatten(items);
                    log("getCustomAttributes -> SET_ATTRIBUTE", items, block);
                    return items;
                }
            });
            attrs = _.flatten(attrs);
            log("getCustomAttributes result before --> ", attrs);
            attrs = attrs
                .filter((x) => !!x)
                .map((x) => x || "")
                .map((x) => x.trim())
                .filter((x) => !_.isEmpty(x));
            log("getCustomAttributes result before 2 --> ", attrs);
            let validAttributes = _.flatMap(attrs.map((x) => this.extractAttributes(x)));
            const systemDefinedAttributes = ["name", "email", "language", "timezone", "gender", "country"];
            validAttributes = validAttributes.filter((x) => !systemDefinedAttributes.includes(x));
            log("getCustomAttributes result --> ", { attrs, validAttributes });
            let uniqueValidAttributes = [...new Set(validAttributes)];
            return uniqueValidAttributes;
        },
        dragStart(ev, type) {
            if (!this.dragMode) {
                log("dragStart", type);
                this.draggingType = type;
                this.dragMode = true;
                ev.dataTransfer.setData("text", type);
            }
        },
        dragEnd(type) {
            log("dragEnd", type);
            this.dragMode = false;
            this.draggingType = null;
        },
        showNotification(messageContent, type = "success", sound = false) {
            type = ["success", "error", "warning", "info"].includes(type) ? type : "success";
            if (sound && this.userInteracted) {
                notificationSound.play();
            }
            message[type](messageContent);
        },
        openNotificationWithIcon(featureType, type = "warning") {
            const messages = {
                openAiApiKeyRequired: {
                    message: this.$t("chatbotv2.gpt3.apiKeyRequired"),
                    description: this.$t("chatbotv2.gpt3.apiKeyDesc"),
                },
            };
            const message = messages[featureType];
            const key = `plan`;
            this.$notification[type]({
                message: message.message,
                description: message.description,
                placement: "topRight",
                key,
                btn: (h) => {
                    let buttonText = "Change Plan";
                    if (featureType === "openAiApiKeyRequired") buttonText = this.$t("chatbotv2.gpt3.enableChatGpt3");
                    return h(
                        "a-button",
                        {
                            props: {
                                type: "primary",
                                size: "small",
                            },
                            on: {
                                click: () => {
                                    if (featureType === "openAiApiKeyRequired") {
                                        this.$router.push("/chatbot/integrations?type=gpt3");
                                    }
                                    this.$notification.close(key);
                                },
                            },
                        },
                        buttonText
                    );
                },
            });
        },
        addBlockToDiagram(blockId, type) {
            this.draggingType = null;
            this.closeModal();
            log("addBlockToDiagram running", blockId, type);
            if (type === BLOCK_TYPES.AI_BOT_RESPONSE && !this.gpt3Enabled) {
                this.openNotificationWithIcon("openAiApiKeyRequired");
                return;
            }
            this.addBlockD({
                id: this.storyId,
                version: this.storyVersion,
                blockId,
                type,
            })
                .then((newBoard) => {
                    log("addBlockToDiagram completed", newBoard);
                    this.showNotification(
                        `${this.$t("chatbotv2.notificationMessageStartSuccess")} ${this.$t(
                            `chatbotv2.${type}`
                        )}  ${this.$t("chatbotv2.notificationMessageEndSuccess")}`,
                        "success",
                        true
                    );
                    SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_FOCUS_BLOCK, {
                        blockId: blockId,
                    });
                })
                .catch((e) => {
                    this.showNotification(
                        `${this.$t("chatbotv2.notificationMessageStartError")} ${this.$t(
                            `chatbotv2.${type}`
                        )} ${this.$t("chatbotv2.notificationMessageEndError")}`,
                        "error",
                        true
                    );
                });
        },
        moveBlock(targetBlockId) {
            const movingBlock = this.flattedDiagram.filter((b) => b.id === this.movingBlock.id)[0];
            const sourceBlock = this.flattedDiagram.filter((b) => b.id === targetBlockId)[0];
            log("move block started", { m: movingBlock, s: sourceBlock });
            if (!this.getAllowedBlocks(sourceBlock).includes(movingBlock.type)) {
                this.showNotification(
                    `The block ${this.movingBlock.name || this.movingBlock.type} has not been moved behind the block ${
                        sourceBlock.name || sourceBlock.type
                    } !`,
                    "error",
                    true
                );
                return;
            }
            this.moveBlockD({
                id: this.storyId,
                version: this.storyVersion,
                sourceBlockId: this.movingBlock.id,
                targetBlockId,
            })
                .then((x) => {
                    this.showNotification(
                        `The block ${this.movingBlock.name || this.movingBlock.type} has been moved`,
                        "success",
                        true
                    );
                })
                .catch((e) => {
                    this.showNotification(
                        `An error occurred, the block ${
                            this.movingBlock.name || this.movingBlock.type
                        } has not been moved`,
                        "error",
                        true
                    );
                })
                .finally(() => {
                    log("move block done");
                    this.changeMoveMode(null);
                });
        },
        copyBlock(targetBlockId) {
            const copyingBlock = this.flattedDiagram.filter((b) => b.id === this.copyingBlock.id)[0];
            const sourceBlock = this.flattedDiagram.filter((b) => b.id === targetBlockId)[0];
            log("copy block started", { m: copyingBlock, s: sourceBlock });
            if (!this.getAllowedBlocks(sourceBlock).includes(copyingBlock.type)) {
                this.showNotification(
                    `The block ${
                        this.copyingBlock.name || this.copyingBlock.type
                    } has not been copied behind the block ${sourceBlock.name || sourceBlock.type} !`,
                    "error",
                    true
                );
                return;
            }
            this.copyBlockD({
                id: this.storyId,
                version: this.storyVersion,
                sourceBlockId: this.copyingBlock.id,
                targetBlockId,
            })
                .then((x) => {
                    this.showNotification(
                        `The block ${this.copyingBlock.name || this.copyingBlock.type} has been copied`,
                        "success",
                        true
                    );
                })
                .catch((e) => {
                    this.showNotification(
                        `An error occurred, the block ${
                            this.copyingBlock.name || this.copyingBlock.type
                        } has not been copied`,
                        "error",
                        true
                    );
                })
                .finally(() => {
                    log("copy block done");
                    this.changeCopyMode(null);
                });
        },
        changeMoveMode(block) {
            if (block) {
                this.movingMode = true;
                this.movingBlock = block;
            } else {
                this.movingMode = false;
                this.movingBlock = null;
            }
        },

        changeCopyMode(block) {
            if (block) {
                this.copyingMode = true;
                this.copyingBlock = block;
            } else {
                this.copyingMode = false;
                this.copyingBlock = null;
            }
        },
        /**
         * ✅ Kural 1 -> EITHER block'lar araya eklenmez
         * ✅️ Kural 2 -> sonlandırıcı block'lar sadece en sona eklenir ve sonrasına bir şey eklenemez.// move nedeniyle eklendi
         * ✅ Kural 3 -> START_POINT her zaman en başta olur,DEFAULT_FALLBACK 1 tanedir.
         * ✅️️ Kural 4 -> START_POINT'ten sonra Flow ve FALLBACK gelemez.
         * ✅️️ Kural 5 -> START_POINT tek çocukluysa sonrasında multiChildBlocks gelebilir.
         * ✅️ Kural 6 -> INPUT blocklar çatallanabilir ve bunlara tek bir tane FALLBACK ve bir tane TIMEOUT eşlik edebilir.
         * ✅️ Kural 7  -> FILTER blocklar çatallanabilir ve FALLBACK eşlik edebilir
         * ✅️️ Kural 8 -> INPUT blocklar,root, BOT_RESPONSE ve SET_ATTRIBUTE den sonra gelebilir
         * ✅️ Kural 9 -> FILTER’dan önce FILTER; FILTER'dan sonra ne FILTER ne FALLBACK gelemez
         * ✅️️ Kural 10 -> INPUT'dan sonra veya önce INPUT gelmez
         * ✅️️ Kural 11 -> EITHER block'larda QUESITON bloğu haric sadece Success,Failure çoçukları olabilir. QUESITON'da TIMEOUT bloğuda gelebilir.
         * ✅️️ Kural 12 -> Yaprak Flow'larda hiç bir şey gözükmez
         * ✅️️ Kural 13 -> Flow'dan sonra flow gelemez
         * ✅️️ Kural 14 -> Flow'dan sonra termination blocks, termination blocks'tan önce Flow, gelemez.
         * ✅️️ Kural 15 -> Action paletteden sürüklenen block, üzerine bırakılacak olan blocka -bu blockun önceki kurallar ile oluşan listesinde- yoksa eklenemez.
         * ✅️️ Kural 16 -> DEFAULT_TIMEOUT yoksa sadece START_POINT'e eklenebilir
         * ✅️️ Kural 16.a -> AI_FUNCTIONS yoksa sadece START_POINT'e eklenebilir
         * ✅️️ Kural 17 -> TIMEOUT bloğu sadece USER_INPUT ATTACHMENT_INPUT olan ve (failure ve succes) olaylarına yerleştirilebilir.
         * ✅️ Kural 18 -> TEMPLATE blockları sadece sona eklenebilir ortaya veya çocuğu olanlara eklenemez.
         */
        getAllowedBlocks(sourceBlock, blocks = BLOCK_TYPES.ALL_BLOCKS) {
            const filteredBlockTypes = blocks.filter((blockType) => blockType !== "AI_ASSISTANT");
            const terminationBlocks = BLOCK_TYPES.TERMINATION_BLOCKS;
            const multiChildBlocks = BLOCK_TYPES.MULTI_CHILD_BLOCKS;
            const isInput = BLOCK_TYPES.INPUT_BLOCKS.includes(sourceBlock.type);
            const isInputChildExits = !!sourceBlock.children.find((child) =>
                BLOCK_TYPES.INPUT_BLOCKS.includes(child.type)
            );
            const isFilter = BLOCK_TYPES.FILTER === sourceBlock.type;
            const isFilterChildExits = !!sourceBlock.children.find((child) => BLOCK_TYPES.FILTER === child.type);
            const isRoot = _.get(this.blocksMetas, `${sourceBlock.id}.isRoot`, false);
            const isFlowRoot = isRoot && sourceBlock.type === BLOCK_TYPES.FLOW;
            const isStartPoint = isRoot && sourceBlock.type === BLOCK_TYPES.START_POINT;
            const isEitherBlock = multiChildBlocks.includes(sourceBlock.type);
            const isMiddleInsertAction = sourceBlock.children.length > 0;
            const isLeaf = sourceBlock.children.length === 0;
            const existsDefaultTimeout = sourceBlock.children.find(
                (block) => block.type === BLOCK_TYPES.DEFAULT_TIMEOUT
            );
            const existsAiFunctionsBlock = sourceBlock.children.find(
                (block) => block.type === BLOCK_TYPES.AI_FUNCTIONS
            );
            const isBlockChildExistsAnyInput = sourceBlock.children.find((b) =>
                BLOCK_TYPES.INPUT_BLOCKS.includes(b.type)
            );

            const allowedBlock = filteredBlockTypes.filter((targetBlockType) => {
                //#region KURAL11
                if (isEitherBlock) {
                    const isTimeoutBlock = targetBlockType === BLOCK_TYPES.TIMEOUT;
                    const EitherBlocks = [BLOCK_TYPES.SUCCESS, BLOCK_TYPES.FAILURE];
                    if (isTimeoutBlock && sourceBlock.type === BLOCK_TYPES.QUESTION)
                        EitherBlocks.push(BLOCK_TYPES.TIMEOUT);
                    if (EitherBlocks.includes(targetBlockType)) {
                        return EitherBlocks.filter(
                            (x) => !sourceBlock.children.map((x) => x.type).includes(x)
                        ).includes(targetBlockType);
                    } else {
                        return false;
                    }
                }

                //#endregion
                //#region KURAL1
                if (!isRoot && isMiddleInsertAction && multiChildBlocks.includes(targetBlockType)) return false;
                //#endregion
                //#region KURAL2
                if (!isLeaf && terminationBlocks.includes(targetBlockType)) {
                    return false;
                }
                if (terminationBlocks.includes(sourceBlock.type)) {
                    return false;
                }
                //#endregione

                //#region KURAL3 &&  KURAl11
                if (
                    [
                        BLOCK_TYPES.START_POINT,
                        BLOCK_TYPES.DEFAULT_FALLBACK,
                        BLOCK_TYPES.SUCCESS,
                        BLOCK_TYPES.FAILURE,
                    ].includes(targetBlockType)
                ) {
                    return false;
                }
                //#endregione
                //#region KURAL4
                if (
                    isRoot &&
                    sourceBlock.type === BLOCK_TYPES.START_POINT &&
                    [BLOCK_TYPES.FLOW, BLOCK_TYPES.FALLBACK].includes(targetBlockType)
                ) {
                    return false;
                }
                //#endregion
                //#region KURAL5
                if (
                    isRoot &&
                    sourceBlock.type === BLOCK_TYPES.START_POINT &&
                    sourceBlock.children.length > 1 &&
                    multiChildBlocks.includes(targetBlockType)
                )
                    return false;
                if (
                    isRoot &&
                    sourceBlock.type === BLOCK_TYPES.FLOW &&
                    isMiddleInsertAction &&
                    multiChildBlocks.includes(targetBlockType)
                )
                    return false;
                //#endregion
                //#region KURAL6 - KURAL7- KURAL14
                const listOfChildren = sourceBlock.children.map((child) => child.type);
                const isFlowBeforeTerminationBlocks =
                    terminationBlocks.some((r) => listOfChildren.indexOf(r) >= 0) &&
                    targetBlockType === BLOCK_TYPES.FLOW;
                if (!isFlowBeforeTerminationBlocks) {
                    const isElseIfPatternDetected =
                        sourceBlock.children.length > 0 &&
                        BLOCK_TYPES.isElseIfBlockPattern(sourceBlock, targetBlockType);

                    if (isElseIfPatternDetected) {
                        const multiFallbackDetected =
                            !!sourceBlock.children.find((x) => x.type === BLOCK_TYPES.FALLBACK) &&
                            targetBlockType === BLOCK_TYPES.FALLBACK;
                        if (targetBlockType === BLOCK_TYPES.FILTER) return !multiFallbackDetected;
                        if (targetBlockType === BLOCK_TYPES.TIMEOUT) {
                            const multiTimeoutDetected =
                                !!sourceBlock.children.find((x) => x.type === BLOCK_TYPES.TIMEOUT) &&
                                targetBlockType === BLOCK_TYPES.TIMEOUT;
                            return !multiTimeoutDetected;
                        }
                        return !multiFallbackDetected;
                    }
                } else return false;
                //#endregion
                //#region KURAL8
                if (
                    !isRoot &&
                    BLOCK_TYPES.INPUT_BLOCKS.includes(targetBlockType) &&
                    ![BLOCK_TYPES.BOT_RESPONSE, BLOCK_TYPES.SET_ATTRIBUTE].includes(sourceBlock.type)
                ) {
                    log("isRoot", { r: isRoot, t: targetBlockType });
                    return false;
                }
                //#endregione
                //#region KURAL9
                if (
                    (isFilter || isFilterChildExits) &&
                    (targetBlockType === BLOCK_TYPES.FILTER || targetBlockType === BLOCK_TYPES.FALLBACK)
                ) {
                    return false;
                }

                //#endregion
                //#region KURAL10
                if ((isInput || isInputChildExits) && BLOCK_TYPES.INPUT_BLOCKS.includes(targetBlockType)) return false;
                //#endregion
                //#region KURAL12
                if (!isFlowRoot && isLeaf && sourceBlock.type === BLOCK_TYPES.FLOW) return false;
                //#endregion
                //#region KURAL13
                const firstChildType = _.get(sourceBlock.children[0], "type", "");
                if (
                    [sourceBlock.type, firstChildType].includes(BLOCK_TYPES.FLOW) &&
                    targetBlockType === BLOCK_TYPES.FLOW
                ) {
                    return false;
                }
                //#endregion
                //#region KURAL14
                if (
                    isFlowRoot &&
                    isLeaf &&
                    sourceBlock.type === BLOCK_TYPES.FLOW &&
                    terminationBlocks.includes(targetBlockType)
                )
                    return false;
                //#endregion

                //#region KURAL 16
                const isDefaultTimeoutBlock = targetBlockType === BLOCK_TYPES.DEFAULT_TIMEOUT;
                if (
                    (!isStartPoint && isDefaultTimeoutBlock) ||
                    (isStartPoint && isDefaultTimeoutBlock && existsDefaultTimeout)
                ) {
                    return false;
                }

                //#region KURAL 16.a
                const isAIFunctionsBlock = targetBlockType === BLOCK_TYPES.AI_FUNCTIONS;
                if (
                    (!isStartPoint && isAIFunctionsBlock) ||
                    (isStartPoint && isAIFunctionsBlock && existsAiFunctionsBlock)
                ) {
                    return false;
                }
                //#endregion

                //#region KURAL 17
                const isTimeoutBlock = targetBlockType === BLOCK_TYPES.TIMEOUT;
                if ((isTimeoutBlock && !isBlockChildExistsAnyInput) || (isTimeoutBlock && !isFilterChildExits)) {
                    return false;
                }
                //#endregion

                //#region KURAL 18
                const isTemplateBlock = BLOCK_TYPES.TAILED_FLOW_BLOCKS.includes(targetBlockType);
                if (isTemplateBlock && !isLeaf) {
                    return false;
                }
                //#endregion

                return true;
            });
            log("drop allowedBlock", allowedBlock);
            return allowedBlock;
        },
        getNavigationDetail() {
            try {
                const navigation = window.location.pathname.replaceAll(
                    `/chatbot/story/${this.storyId}/${this.storyVersion}/`
                );
                const flowRegex = new RegExp("flow\/([A-z]|[0-9]|-)+", "gmi");
                const blockRegex = new RegExp("block\/([A-z]|[0-9]|-)+", "gmi");
                let selectedFlow = null;
                let selectedFlowPath = null;
                let selectedBlock = null;
                let parentFlows = [];
                const storyId = this.storyId;
                const storyVersion = this.storyVersion;
                if (flowRegex.test(navigation)) {
                    selectedFlow = navigation.match(flowRegex)[0].replaceAll("flow/", "");
                    if (selectedFlow) {
                        let foundBlock = this.flattedDiagram.find((block) => block.id === selectedFlow);
                        if (foundBlock) {
                            selectedFlowPath = foundBlock.path;
                            while (!!foundBlock.parent) {
                                if (foundBlock.type === "FLOW") {
                                    parentFlows.push({
                                        flowId: foundBlock.id,
                                        path: foundBlock.path,
                                        block: _.get(this.board.diagram, foundBlock.path),
                                        navigationPath: `/chatbot/story/${storyId}/${storyVersion}/flow/${foundBlock.id}`,
                                    });
                                }
                                foundBlock = foundBlock.parent;
                            }
                        }
                    }
                }
                if (blockRegex.test(navigation)) {
                    selectedBlock = navigation.match(blockRegex)[0].replaceAll("block/", "");
                }

                const navigationDetail = {
                    selectedFlow,
                    selectedFlowPath,
                    selectedBlock,
                    parentFlows,
                    blockNavigationPath: navigation,
                    pathname: this.$route.path,
                    storyId: this.storyId,
                    storyVersion: this.storyVersion,
                };
                if (
                    !!this.latestNavigationDetail &&
                    StringUtils.isDeepEqual(this.latestNavigationDetail, navigationDetail)
                ) {
                    return this.latestNavigationDetail;
                }
                this.latestNavigationDetail = navigationDetail;
                // log("navigationDetail", navigationDetail);
                return navigationDetail;
            } catch (e) {
                console.error("getNavigationDetail error", e);
                return null;
            }
        },
        openAddContext({ e, block }) {
            this.addMenuPos.x = e.x;
            this.addMenuPos.y = e.y;
            const diffY = window.innerHeight - e.y;
            this.addMenuPos.height = `${diffY}px`;
            if (diffY < 200) {
                this.addMenuPos.y = e.y - 200;
                this.addMenuPos.height = `${diffY + 200}px`;
            }

            this.addMenu = true;
            this.overlayVisible = true;
            this.addBlock = block;
        },
        updateTranslate(e, isSet = false) {
            log("updateTranslate", { e, isSet });
            if (isSet) {
                this.oldTranslate = _.clone(this.translate);
                this.smoothTranslate(e, true);
            } else {
                this.translate.x += e.x / this.scale;
                this.translate.y += e.y / this.scale;
            }
        },
        openModal(block) {
            log("openModal", block);
            if (this.block !== block) {
                this.block = block;
                this.navigateToBlock(block);
                this.overlayVisible = true;
            } else this.closeModal();
        },
        closeModal(noRequest = true) {
            if (!noRequest && !!this.$refs.BlockModal) {
                this.$refs.BlockModal.updatePayload(true).then((x) => {
                    if (x)
                        this.$toasted.success(this.$t("chatbotv2.blockUpdated"), {
                            duration: 2000,
                            position: "bottom-center",
                        });
                });
            }
            // if (!this.addMenu)
            //     this.smoothTranslate(
            //         {
            //             x: this.oldTranslate.x,
            //             y: this.oldTranslate.y,
            //         },
            //         true
            //     );
            if (this.block) {
                this.removeBlockFromNavigation(this.block.id);
            }
            this.block = null;
            this.addBlock = null;
            this.addMenu = false;
            this.overlayVisible = false;
        },
        smoothTranslate(e, isGOTO = false) {
            let counter = 0;
            let localScale = !isGOTO ? this.scale : 1;
            const newX = e.x / localScale;
            const newY = e.y / localScale;
            const diffX = Math.abs(this.translate.x - newX);
            const diffY = Math.abs(this.translate.y - newY);
            this.translating = true;
            const stepSize = 15;
            let timer = setInterval(() => {
                if (counter === stepSize) {
                    clearInterval(timer);
                    this.translating = false;
                    return;
                }
                if (this.translate.x < newX) this.translate.x += diffX / stepSize;
                else this.translate.x -= diffX / stepSize;
                if (this.translate.y < newY) this.translate.y += diffY / stepSize;
                else this.translate.y -= diffY / stepSize;
                counter++;
            }, 10);
        },
        navigateToBlock(block, autoFocus = true) {
            log("navigateToBlock", block);
            if (!block) {
                const pathname = `/chatbot/story/${this.storyId}/${this.storyVersion}`;
                this.$router.push(pathname);
            } else {
                const {
                    selectedFlow,
                    selectedBlock,
                    parentFlows,
                    blockNavigationPath,
                    pathname,
                    storyId,
                    storyVersion,
                } = this.getNavigationDetail();
                log("navigateToBlock", block, {
                    selectedFlow,
                    selectedBlock,
                    parentFlows,
                    blockNavigationPath,
                    pathname,
                    storyId,
                    storyVersion,
                });
                if (block.type === "FLOW") {
                    const pathname = `/chatbot/story/${storyId}/${storyVersion}/flow/${block.id}`;
                    log("flow navigation to", pathname);
                    this.$router.push(pathname);
                    if (autoFocus) {
                        SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_FOCUS_BLOCK, {
                            blockId: _.get(this.board.diagram, "id"),
                        });
                    }
                } else {
                    const pathname = `${window.location.pathname}/block/${block.id}`;
                    log("block navigation to", pathname);
                    this.$router.push(pathname);
                }
            }
        },
        removeBlockFromNavigation(blockId) {
            const pathname = window.location.pathname.replaceAll(`/block/${blockId}`, "");
            this.$router.push(pathname);
        },
        subscribeSocketEvents() {
            this.socketEventSubscriber = socketMessageStream
                .pipe(filter((x) => x.cmd.startsWith("chatbot/v2")))
                .subscribe(({ cmd, data }) => {
                    if (cmd === "chatbot/v2 state_changed") {
                        // if (_.get(this.testState, "position") !== data.position) {
                        //     SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_FOCUS_BLOCK, {
                        //         blockId: data.position,
                        //     });
                        // }
                        if (this.chatWidgetOpened) {
                            this.testState = data;
                            log("test state", this.testState);
                        }
                    }
                });
        },
        subscribeEventBus() {
            this.eventBusSubscriber = SYSTEM_PUBSUB.stream.subscribe(({ type, payload }) => {
                if (type === SYSTEM_PUBSUB.MESSAGE_TYPES.CB_SELECT_BLOCK) {
                    this.selectedBlockId = payload.blockId;
                } else if (type === SYSTEM_PUBSUB.MESSAGE_TYPES.CB_CHAT_WIDGET_OPEN_STATE_CHANGED) {
                    this.chatWidgetOpened = payload.visible;
                } else if (type === SYSTEM_PUBSUB.MESSAGE_TYPES.CB_GET_CUSTOM_ATTRIBUTES) {
                    SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_GET_CUSTOM_ATTRIBUTES_RESPONSE, {
                        attributes: this.getCustomAttributes(),
                    });
                }
            });
        },
        startTestFromHere(block) {
            log("startTestFromHere", block);
            this.testStartBlockId = block.id;
        },
        focusStartPoint() {
            this.scale = 1;
            this.oldTranslate = _.cloneDeep(initialTranslate);
            this.translate = _.cloneDeep(initialTranslate);
            const pathname = `/chatbot/story/${this.storyId}/${this.storyVersion}`;
            this.$router.push(pathname);
        },
        startVisitorStory() {
            this.storyVisitSub = setInterval(() => {
                sendSocketMessage("user join-story", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    storyId: this.storyId,
                });
            }, 3 * 1000);
        },
    },
    mounted() {
        const { selectedFlow, selectedBlock } = this.getNavigationDetail();
        if (selectedBlock) {
            const path = _.get(
                this.flattedDiagram.find((block) => block.id === selectedBlock),
                "path"
            );
            const block = _.get(this.board.diagram, path);
            if (block) this.openModal(block);
        }
        this.subscribeSocketEvents();
        this.subscribeEventBus();
        this.fetchUserEntities();
        this.startVisitorStory();
    },
    beforeDestroy() {
        this.socketEventSubscriber.unsubscribe();
        this.eventBusSubscriber.unsubscribe();
        clearInterval(this.storyVisitSub);
    },
    watch: {
        scale(newValue, oldValue) {
            // if (newValue <= 0.5 || newValue >= 1.5)
            if (newValue < 0.5) {
                this.scale = 0.5;
            } else if (newValue > 1.5) {
                this.scale = 1.5;
            } else {
                if (this.scale !== newValue) this.scale = newValue;
            }
        },
        chatWidgetOpened(isOpened) {
            if (!isOpened) {
                this.testState = null;
                this.testStartBlockId = null;
            }
        },
        testState: {
            handler(newState, oldState) {
                log("testState changed", { oldState, newState, flattedDiagram: this.flattedDiagram });
                const oldPosition = _.get(oldState, "position");
                const newPosition = _.get(newState, "position");
                if (!!newPosition && oldPosition !== newPosition) {
                    console.log("findParentContainer", {
                        newPosition,
                        oldPosition,
                        flattedDiagram: this.flattedDiagram,
                    });
                    const block = this.flattedDiagram.find((block) => block.id === newPosition);
                    if (block.isAIAssistant) {
                        this.navigateToBlock(null);
                        return;
                    }
                    const findParentContainer = (block) => {
                        console.log("findParentContainer", block);
                        const parent = block.parent;
                        if (parent.type === BLOCK_TYPES.FLOW || parent.type === BLOCK_TYPES.START_POINT) {
                            return parent;
                        } else {
                            return findParentContainer(parent);
                        }
                    };
                    const parentContainer = findParentContainer(block);
                    if (parentContainer.type === BLOCK_TYPES.FLOW) {
                        const navigationDetail = this.getNavigationDetail();
                        if (navigationDetail.selectedFlow !== parentContainer.id) {
                            this.navigateToBlock(parentContainer);
                        }
                    } else {
                        this.navigateToBlock(null);
                    }

                    setTimeout(() => {
                        SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_FOCUS_BLOCK, {
                            blockId: block.id,
                        });
                    }, 500);

                    // if (block) this.navigateToBlock(block);
                }
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.overlay {
    background-color: #00000066;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 9;
}
</style>
